
import React, { createContext, useContext, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { User } from '@socialflows/shared-lib/src/entities/user.entity';
import { authService } from '@socialflows/shared-lib';
import type { RegisterInput, LoginInput } from '@socialflows/shared-lib/src/services/auth.service';

type AuthContextType = {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (data: LoginInput) => Promise<void>;
  register: (data: RegisterInput) => Promise<void>;
  logout: () => Promise<void>;
};

const AUTH_QUERY_KEY = ['auth', 'me'];

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const queryClient = useQueryClient();

  const { data: user = null, isLoading } = useQuery<User | null>({
    queryKey: AUTH_QUERY_KEY,
    queryFn: async () => {
      try {
        const session = await authService.getCurrentUser();
        if (session?.user) {
          return session.user as unknown as User;
        }
        return null;
      } catch {
        return null;
      }
    },
    retry: false,
    refetchOnWindowFocus: false,
  });

  const login = useCallback(
    async (data: LoginInput) => {
      const response = await authService.login(data);
      queryClient.setQueryData(AUTH_QUERY_KEY, response.user as unknown as User);
    },
    [queryClient]
  );

  const register = useCallback(async (data: RegisterInput) => {
    await authService.register(data);
  }, []);

  const logout = useCallback(async () => {
    await authService.logout();
    queryClient.setQueryData(AUTH_QUERY_KEY, null);
  }, [queryClient]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        login,
        register,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
